// src/layouts/SidebarLayout.tsx
import { useState, useEffect } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useUnreadTickets } from "../hooks/useUnreadTickets";
import ThemePanel from "@/theme/ThemePanel";

const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  return isMobile;
};

import {
  HomeIcon as Home,
  PersonIcon as Users,
  CardStackIcon as CreditCard,
  PlayIcon as Play,
  ClockIcon as Clock,
  GearIcon as Settings,
  LockClosedIcon as Shield,
  ExitIcon as Logout,
  HamburgerMenuIcon as Menu,
  ExclamationTriangleIcon as Alert,
  DownloadIcon as Download,
  ChatBubbleIcon as Support,
  BarChartIcon as ChartBar,
} from "@radix-ui/react-icons";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const NavItem = ({
  to,
  label,
  icon: Icon,
  badge,
  collapsed,
}: {
  to: string;
  label: string;
  icon: IconType;
  badge?: number;
  collapsed?: boolean;
}) => (
  <NavLink
    to={to}
    className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""} ${collapsed ? "justify-center" : ""}`}
  >
    <Icon className="w-5 h-5 shrink-0" />
    {!collapsed && <span className="flex-1">{label}</span>}
    {badge && badge > 0 && !collapsed && (
      <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </NavLink>
);

export default function SidebarLayout() {
  const { logout, user } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const unreadTicketsCount = useUnreadTickets();

  useEffect(() => {
    // Close mobile drawer when route changes, but don't auto-collapse on desktop
    setSidebarOpen(false);
  }, [isMobile]);

  const handleLogout = async () => {
    await logout();
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    }
    // Desktop sidebar is always expanded - no toggle needed
  };

  return (
    // IMPORTANT: scope theme variables here
    <div className="admin-dashboard min-h-screen bg-background">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile header with hamburger */}
      {isMobile && (
        <div className="sticky top-0 z-30 flex items-center gap-3 bg-card border-b border-border px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-foreground" />
          </button>
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-lg font-bold text-foreground">Handpicked Admin</h1>
        </div>
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside 
          className={`
            border-r border-border bg-card/50 transition-all duration-300 ease-in-out
            ${isMobile 
              ? `fixed top-0 left-0 h-full z-50 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-64`
              : 'relative w-64 h-screen'
            }
          `}
        >
          <div className="h-full min-h-0 flex flex-col overflow-hidden p-4">
            {/* Desktop header */}
            {!isMobile && (
              <div className="flex items-center gap-2 mb-8">
                <Shield className="w-8 h-8 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold text-foreground truncate">Handpicked Admin</h1>
                  <p className="text-sm text-muted-foreground truncate">Kids Video Dashboard</p>
                </div>
              </div>
            )}

            {/* Mobile header */}
            {isMobile && (
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Handpicked Admin</h1>
                  <p className="text-sm text-muted-foreground">Kids Video Dashboard</p>
                </div>
              </div>
            )}

            {/* Scrollable nav */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden space-y-1 min-h-0">
              <NavItem to="/dashboard" label="Dashboard" icon={Home} collapsed={false} />
              <NavItem to="/users" label="Users" icon={Users} collapsed={false} />
              <NavItem to="/user-management" label="User Management" icon={Users} collapsed={false} />
              <NavItem to="/subscriptions" label="Payments" icon={CreditCard} collapsed={false} />
              <NavItem to="/videos" label="Videos" icon={Play} collapsed={false} />
              <NavItem to="/content-manager" label="Content Manager" icon={Play} collapsed={false} />
              <NavItem to="/reports" label="Video Reports" icon={Alert} collapsed={false} />
              <NavItem to="/entitlements" label="Entitlements" icon={Shield} collapsed={false} />
              <NavItem to="/promo-codes" label="Promo Codes" icon={CreditCard} collapsed={false} />
              <NavItem to="/affiliates" label="Affiliates" icon={Users} collapsed={false} />
              <NavItem to="/feature-flags" label="Feature Flags" icon={Settings} collapsed={false} />
              <NavItem to="/user-cohorts" label="User Cohorts" icon={Users} collapsed={false} />
              <NavItem to="/marketing" label="Marketing" icon={Menu} collapsed={false} />
              <NavItem to="/pricing" label="Pricing" icon={CreditCard} collapsed={false} />
              <NavItem to="/activity" label="Activity" icon={Clock} collapsed={false} />
              <NavItem to="/force-messages" label="Force Messages" icon={Alert} collapsed={false} />
              <NavItem to="/download-manager" label="Download Manager" icon={Download} collapsed={false} />
              <NavItem to="/downloads-analytics" label="Downloads Analytics" icon={ChartBar} collapsed={false} />
              <NavItem to="/support-tickets" label="Support Tickets" icon={Support} badge={unreadTicketsCount} collapsed={false} />
              <NavItem to="/impersonation" label="User Impersonation" icon={Shield} collapsed={false} />
              <NavItem to="/settings" label="Settings" icon={Settings} collapsed={false} />
            </nav>

            {/* Bottom info */}
            {!isMobile && (
              <>
                <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="text-xs text-primary font-medium">Live Ops Stack</div>
                  <div className="text-xs text-muted-foreground mt-1">Auth and admin data: Supabase</div>
                  <div className="text-xs text-muted-foreground">Video catalog: Cloudflare</div>
                </div>

                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Logged in as:</div>
                  <div className="text-sm font-medium text-foreground truncate">
                    {user?.email}
                  </div>

                  <button
                    onClick={handleLogout}
                    className="mt-2 w-full btn btn--danger btn--sm flex items-center justify-center gap-2"
                  >
                    <Logout className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 bg-background overflow-auto">
          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating theme button + panel */}
      <ThemePanel />
    </div>
  );
}
